package structs.account

import kotlinx.serialization.Serializable

/**
 * Subscription tier for a player's account.
 *
 * Tier names are thematically aligned with the game's setting (geopolitical
 * strategy, autocracy, satire of real-world politics). The five-tier ladder
 * mirrors the subscription-model research at
 * `tools/autogenesis_subscription_model.html` (Section 4.5).
 *
 * Each tier carries:
 * - [tokenCapPerCycle] the monthly inference budget the player can burn
 * - [unlocksModes] the set of game modes the tier is permitted to start
 * - [pricePerMonthUsd] the headline price, used for tier-mix revenue projections
 * - [maxConcurrentGames] the maximum number of games a player on this tier may
 *   have in progress simultaneously. Used by [accounting.ConcurrentGameGate] to
 *   reject new game starts once the per-player count hits the tier's limit.
 *
 * @see structs.account.BillingStatus
 */
@Serializable
enum class AccountPlan(
    val tokenCapPerCycle: Long,
    val pricePerMonthUsd: Double,
    val unlocksModes: Set<GameMode>,
    val maxConcurrentGames: Int
)
{
    /** Free tier — multiplayer only, smallest monthly token budget. */
    FREE(
        tokenCapPerCycle = 2_000_000L,
        pricePerMonthUsd = 0.0,
        unlocksModes = setOf(GameMode.MULTIPLAYER),
        maxConcurrentGames = 1
    ),

    /** Entry paid tier ($25/mo). Multiplayer + 1v1 single-player, larger cap than FREE. */
    JUNTA(
        tokenCapPerCycle = 70_000_000L,
        pricePerMonthUsd = 25.0,
        unlocksModes = setOf(GameMode.MULTIPLAYER, GameMode.ONE_V_ONE),
        maxConcurrentGames = 1
    ),

    /** Mid-tier ($50/mo). Same modes as JUNTA, larger cap. */
    REPUBLIC(
        tokenCapPerCycle = 140_000_000L,
        pricePerMonthUsd = 50.0,
        unlocksModes = setOf(GameMode.MULTIPLAYER, GameMode.ONE_V_ONE),
        maxConcurrentGames = 1
    ),

    /** Heavy tier ($75/mo). Unlocks 1v3 single-player. */
    EMPIRE(
        tokenCapPerCycle = 210_000_000L,
        pricePerMonthUsd = 75.0,
        unlocksModes = setOf(GameMode.MULTIPLAYER, GameMode.ONE_V_ONE, GameMode.ONE_V_THREE),
        maxConcurrentGames = 2
    ),

    /** Top tier ($100/mo). Full access, largest cap. */
    SOVEREIGN(
        tokenCapPerCycle = 280_000_000L,
        pricePerMonthUsd = 100.0,
        unlocksModes = GameMode.entries.toSet(),
        maxConcurrentGames = 3
    )
}
